import { isAuthAvailable, subscribeAuth, type AuthUser } from "./authClient";
import { subscribeSharedInbox } from "./sharedRoomsCloud";
import type { SharedInboxEntry } from "./sharedRoomTypes";

/**
 * Authoritative client-side mirror of `users/{uid}/sharedInbox` — the
 * set of `sharedRooms/{roomId}` documents the signed-in user is a
 * member of. The inbox is maintained server-side by the share Cloud
 * Functions in `functions/src/share.ts` (`createSharedRoom`,
 * `acceptShareInvite`, `removeShareMember`, `leaveSharedRoom`,
 * `deleteSharedRoom`), so it is always in sync with `members` on the
 * shared room doc.
 *
 * This registry exists so the rest of the app can decide whether a
 * given roomId is "shared" without a Firestore round-trip on every
 * room-open. Shared mode used to be gated by the `?shared=` URL param,
 * which meant opening a previously-shared room from the saved library
 * never started multiplayer sync. With this registry, the data model
 * — not the URL — drives the decision.
 *
 * Lifecycle (modeled on `librarySync.ts`):
 *   - On sign-in: attach `subscribeSharedInbox` and rebuild the set on
 *     every snapshot.
 *   - On sign-out: clear the set and notify subscribers.
 *
 * Safe to call `initSharedRoomsRegistry()` multiple times (idempotent).
 */

let initialized = false;
let unsubAuth: (() => void) | null = null;
let unsubInbox: (() => void) | null = null;
let activeUid: string | null = null;

let mySharedRoomIds: ReadonlySet<string> = new Set();
const listeners = new Set<(ids: ReadonlySet<string>) => void>();

function emit(): void {
  for (const l of listeners) l(mySharedRoomIds);
}

/** Synchronous check: is `roomId` currently in the user's inbox? */
export function isSharedRoom(roomId: string): boolean {
  return mySharedRoomIds.has(roomId);
}

/** Read the current set of shared room ids the user is a member of. */
export function getMySharedRoomIds(): ReadonlySet<string> {
  return mySharedRoomIds;
}

/**
 * Subscribe to membership changes. The callback receives the latest
 * set every time the inbox snapshot rebuilds (and on sign-in / sign-out).
 * Returns an unsubscribe function.
 */
export function subscribeRegistry(
  cb: (ids: ReadonlySet<string>) => void
): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Entry point — wire up auth → inbox subscription. Idempotent. */
export function initSharedRoomsRegistry(): void {
  if (initialized) return;
  initialized = true;
  if (!isAuthAvailable()) return;
  unsubAuth = subscribeAuth((user) => {
    handleAuthChange(user);
  });
}

function handleAuthChange(user: AuthUser | null): void {
  if (unsubInbox) {
    unsubInbox();
    unsubInbox = null;
  }
  activeUid = user?.uid ?? null;

  if (!user) {
    if (mySharedRoomIds.size > 0) {
      mySharedRoomIds = new Set();
      emit();
    }
    return;
  }

  const uid = user.uid;
  unsubInbox = subscribeSharedInbox(
    uid,
    (entries) => {
      if (activeUid !== uid) return;
      applyEntries(entries);
    },
    () => {
      if (activeUid !== uid) return;
      // Inbox listener errored (network blip, transient permissions).
      // Leave the last-known set in place rather than silently dropping
      // every shared-room link — the snapshot will recover on retry.
    }
  );
}

function applyEntries(entries: SharedInboxEntry[]): void {
  const next = new Set<string>();
  for (const entry of entries) next.add(entry.roomId);
  if (setsEqual(mySharedRoomIds, next)) return;
  mySharedRoomIds = next;
  emit();
}

function setsEqual(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

/**
 * Test-only: tear down all subscriptions and reset module state so each
 * test starts from a clean slate.
 */
export function _resetSharedRoomsRegistryForTests(): void {
  if (unsubInbox) {
    unsubInbox();
    unsubInbox = null;
  }
  if (unsubAuth) {
    unsubAuth();
    unsubAuth = null;
  }
  initialized = false;
  activeUid = null;
  mySharedRoomIds = new Set();
  listeners.clear();
}
